/**
 * Admin: cycles, grants, audit.
 *
 * Everything here is refused by the server for anyone whose `role` is not
 * `admin`, so the guard in the route file is navigation, not protection.
 */
import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import { messageOf } from "./errors";

async function fetchAdmin() {
  const { data, error } = await api.admin.get();
  if (error) throw new Error(messageOf(error.value, "Could not load the admin view."));
  return data;
}

export const adminQuery = queryOptions({ queryKey: ["admin"], queryFn: fetchAdmin });

export type AdminView = Awaited<ReturnType<typeof fetchAdmin>>;

async function fetchAudit(limit: number) {
  const { data, error } = await api.admin.audit.get({ query: { limit } });
  if (error) throw new Error(messageOf(error.value, "Could not load the audit log."));
  return data;
}

export const auditQuery = (limit = 100) =>
  queryOptions({ queryKey: ["admin", "audit", limit], queryFn: () => fetchAudit(limit) });

export function useAdminMutations() {
  const qc = useQueryClient();
  const done = () => qc.invalidateQueries({ queryKey: ["admin"] });

  const createCycle = useMutation({
    mutationFn: async (name: string) => {
      const { data, error } = await api.admin.cycles.post({ name });
      if (error) throw new Error(messageOf(error.value, "Could not create that cycle."));
      return data;
    },
    onSuccess: done,
  });

  const activateCycle = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await api.admin.cycles({ id }).activate.post();
      if (error) throw new Error(messageOf(error.value, "Could not activate that cycle."));
      return data;
    },
    // Grants, applicant lists and the nav all resolve against the active
    // cycle, so this one invalidates everything rather than just ["admin"].
    onSuccess: () => qc.invalidateQueries(),
  });

  const grant = useMutation({
    mutationFn: async (input: { vertical: string; email: string }) => {
      const { data, error } = await api.admin.coordinators.post(input);
      if (error) throw new Error(messageOf(error.value, "Could not grant that."));
      return data;
    },
    onSuccess: done,
  });

  const revoke = useMutation({
    mutationFn: async (input: { vertical: string; userId: string }) => {
      const { data, error } = await api.admin.coordinators.delete(input);
      if (error) throw new Error(messageOf(error.value, "Could not revoke that."));
      return data;
    },
    onSuccess: done,
  });

  return { createCycle, activateCycle, grant, revoke };
}
